import {
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  alpha,
  Avatar,
  DialogActions,
  Button,
  Tooltip,
} from "@mui/material";
import moment from "moment";
import React from "react";
import { GapBadge, TherapyChip, VisitTypeBadge } from "../../../atoms/Chip";
import { formatDateUI, formatTime } from "../../../helpers/dateFormat";
import { backgroundColourCheck, getColorForRxNumber } from "../../../helpers/statusColor";
import { getContrastTextColor } from "../../../themes/colorUtils";
import { getStatusColor } from "../../PatientOverview/RxInfo/RxForms/statusColor";
import { Close, EventBusy } from "@mui/icons-material";
import Controls from "../../../atoms/Controls";
import { TimeSlotDto } from "../../../api-client";
import { AnyDataTag } from "@tanstack/react-query";
import { slotTherapy, therapyColorKey } from "../../../helpers/slotTherapy";

interface GapPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  data: {
    previous: any | null;
    proposed: any | null;
    next: any | null;
  } | null;
}

const getSlotGapInDays = (
  appointments: any[],
  appointmentId: string,
  slotId: string
): number | null => {
  //  Find current appointment
  const currentAppointment = appointments.find((a) => a?.id === appointmentId);
  if (!currentAppointment) return null;

  //  Find current slot
  const currentSlot = currentAppointment.timeSlots?.find((s: any) => s.id === slotId);
  if (!currentSlot?.start) return null;

  //  Sort future appointments by start date
  const futureAppointments = appointments
    .filter((a) => a?.start && moment(a.start).isAfter(moment(currentAppointment.start)))
    .sort((a, b) => moment(a.start).valueOf() - moment(b.start).valueOf());

  //  Find FIRST appointment that contains same series slot
  for (const appointment of futureAppointments) {
    const matchingSlot = appointment.timeSlots?.find(
      (s: any) =>
        s.treatmentIndex === currentSlot.treatmentIndex ||
        s.therapyRx?.rx?.rxSeries?.id === currentSlot.therapyRx?.rx?.rxSeries?.id
    );

    if (matchingSlot?.start) {
      //  Calculate gap
      return moment(matchingSlot.start)
        .startOf("day")
        .diff(moment(currentSlot.start).startOf("day"), "days");
    }
  }

  return null;
};

const GapPreviewDialog = ({ open, onClose, data }: GapPreviewDialogProps) => {
  if (!data) return null;

  const rows = [
    { label: "Vorheriger Termin", appointment: data.previous },
    { label: "Neuer Termin (Lücke)", appointment: data.proposed },
    { label: "Nächster Termin", appointment: data.next },
  ];
  // console.debug("GapPreviewDialog Rendered with data:", data, rows);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        Termin-Einordnung
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center">Lücke</TableCell>
                <TableCell align="center">Datum</TableCell>
                <TableCell align="center">Startzeit</TableCell>
                <TableCell align="center">Endzeit</TableCell>
                <TableCell align="center">Rezept</TableCell>
                <TableCell align="center">Rx-Datum</TableCell>
                <TableCell align="center">Index</TableCell>
                <TableCell align="center">Therapie</TableCell>
                <TableCell align="center">Therapeut</TableCell>
                <TableCell align="center">Typ</TableCell>
                <TableCell align="center">Status</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map(({ label, appointment }, index) => (
                <React.Fragment key={label}>
                  <TableRow>
                    <TableCell colSpan={10} sx={{ fontWeight: "bold" }}>
                      {label}
                    </TableCell>
                  </TableRow>

                  {appointment ? (
                    appointment.timeSlots?.map((slot: any, slotIndex: number) => {
                      const rxId = slot?.therapyRx?.rx?.id;
                      const appointments = [data?.previous, data?.proposed, data?.next].filter(
                        Boolean
                      );

                      const gapInDays = (slot: TimeSlotDto) =>
                        getSlotGapInDays(appointments, appointment?.id, slot?.id!);
                      const slotGapInDays = gapInDays(slot);
                      const baseGapColor = backgroundColourCheck(
                        typeof slotGapInDays === "number" ? slotGapInDays / 7 : 0
                      );

                      const gapColor = slot.start
                        ? baseGapColor
                          ? alpha(baseGapColor, 0.5)
                          : baseGapColor
                        : undefined;

                      return (
                        <TableRow key={`${index}-${slotIndex}`}>
                          <TableCell align="center">
                            <GapBadge gapInDays={slotGapInDays} gapColor={gapColor} />
                          </TableCell>
                          {slotIndex === 0 && (
                            <TableCell
                              rowSpan={appointment.timeSlots.length}
                              align="center"
                              sx={{
                                verticalAlign: "top", // align at the top of the rowspan cell
                                padding: "4px",
                              }}
                            >
                              <Controls.Chip
                                sx={{
                                  fontSize: 18,
                                  padding: "0px 8px",
                                  borderRadius: "0.5em",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                                label={
                                  appointment.start && !appointment.unschedule
                                    ? formatDateUI(appointment.start)
                                    : "-"
                                }
                              />
                            </TableCell>
                          )}

                          <TableCell align="center">
                            <Controls.Chip
                              sx={{
                                fontSize: 18,
                                padding: "0px 0px",
                                borderRadius: "0.5em",
                                alignItems: "center",
                                display: "flex",
                              }}
                              label={slot.start ? `${formatTime(slot.start)}` : "nicht geplant"}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Controls.Chip
                              sx={{
                                fontSize: 18,
                                padding: "0px 0px",
                                borderRadius: "0.5em",
                                alignItems: "center",
                                display: "flex",
                              }}
                              label={slot.end ? `${formatTime(slot.end)}` : "nicht geplant"}
                            />
                          </TableCell>

                          <TableCell align="center">
                            {slot.therapyRx?.rx?.rxNumber && (
                              <Tooltip title={slot.therapyRx?.rx?.rxNumber}>
                                <Controls.Chip
                                  label={slot.therapyRx?.rx?.rxNumber || "-"}
                                  sx={{
                                    backgroundColor: getColorForRxNumber(
                                      slot.therapyRx?.rx?.rxNumber
                                    ),
                                    color: getContrastTextColor(
                                      getColorForRxNumber(slot.therapyRx?.rx?.rxNumber)
                                    ),
                                    fontSize: 18,
                                    direction: "rtl",
                                  }}
                                  variant="outlined"
                                />
                              </Tooltip>
                            )}
                          </TableCell>

                          <TableCell align="center">
                            {slot.therapyRx?.rx?.date ? (
                              <Tooltip title={slot.therapyRx?.rx?.date}>
                                <Controls.Chip
                                  sx={{
                                    fontSize: 18,
                                    padding: "0px 0px",
                                    borderRadius: "0.5em",
                                    alignItems: "center",
                                    display: "flex",
                                    backgroundColor: slot.therapyRx?.rx?.rxNumber
                                      ? getColorForRxNumber(slot.therapyRx?.rx?.rxNumber)
                                      : "default",
                                  }}
                                  label={moment(slot.therapyRx?.rx?.date).format("DD.MM.YYYY")}
                                />
                              </Tooltip>
                            ) : (
                              <Tooltip title="Kein Datum">
                                <Controls.Chip
                                  sx={{
                                    fontSize: 18,
                                    padding: "0px 0px",
                                    borderRadius: "0.5em",
                                    alignItems: "center",
                                    display: "flex",
                                    backgroundColor: slot.therapyRx?.rx?.rxNumber
                                      ? getColorForRxNumber(slot.therapyRx?.rx?.rxNumber)
                                      : "default",
                                  }}
                                  label={<EventBusy />}
                                />
                              </Tooltip>
                            )}
                          </TableCell>

                          <TableCell align="center">
                            {" "}
                            <Controls.Chip
                              sx={{
                                fontSize: 18,
                                padding: "0px 0px",
                                borderRadius: "0.5em",
                                alignItems: "center",
                                display: "flex",
                              }}
                              label={slot.treatmentIndex ? slot.treatmentIndex : "-"}
                            />
                          </TableCell>

                          <TableCell align="center">
                            <TherapyChip
                              sx={{ fontSize: 18, padding: "0px 0px", borderRadius: "0.5em" }}
                              therapyName={slotTherapy(slot)?.abbreviation || "-"}
                              colorKey={therapyColorKey(slot)}
                            />
                          </TableCell>

                          <TableCell align="center">
                            {slot?.user?.id ? (
                              <Controls.Chip
                                sx={{
                                  fontSize: 18,
                                  padding: "0px 0px",
                                  borderRadius: "0.5em",
                                  alignItems: "center",
                                  display: "flex",
                                }}
                                label={
                                  slot?.user ? `${slot.user.firstName} ${slot.user.lastName}` : "-"
                                }
                              />
                            ) : (
                              "-"
                            )}
                          </TableCell>

                          <TableCell align="center">
                            <VisitTypeBadge
                              visitType={appointment?.address?.type}
                              visitFee={slot?.visitFee?.abbreviation}
                            />
                          </TableCell>

                          <TableCell align="center">
                            <Avatar
                              sx={{
                                width: 24,
                                height: 24,
                                backgroundColor: getStatusColor(
                                  slot.therapyRx?.rx?.processingStatus
                                ),
                                "& > *": { display: "none" },
                              }}
                            >
                              <React.Fragment></React.Fragment>
                            </Avatar>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={10} align="center">
                        Kein Termin vorhanden
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};
export default GapPreviewDialog;
